/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  interpret,
  Machine,
  State,
  Interpreter,
  forwardTo,
  assign,
} from 'xstate';
import { inspect } from '@xstate/inspect';
import { isProduction, reportError, throwErrorWithId } from '@pypestream/utils';
import { initialize } from 'launchdarkly-js-client-sdk';
import {
  AvailableFeatureFlagKeys,
  FeatureFlagsType,
} from '@pypestream/feature-flags';

import { type WindowWithPypestreamGlobals } from './global';
import {
  userMachine,
  type UserInterpreter,
  createUserXstateHooks,
  type UserContext,
  type UserEvents,
  type UserState,
  type UserTypestates,
} from './user.xstate';
import {
  type GlobalAppContext,
  type GlobalAppEvents,
  type GlobalAppStateSchema,
  type GlobalAppTypestates,
  globalAppActions,
  appActivities,
  sendUserMessage,
  createAppXstateHooks,
  GLOBAL_APP_SUB_MACHINE_IDS,
} from './app.xstate-utils';
import {
  ProStudioContext,
  ProStudioEvents,
  type ProStudioInterpreter,
  ProStudioState,
  ProStudioTypestates,
} from './prostudio.xstate.types';
import {
  createProStudioXstateHooks,
  proStudioMachine,
} from './prostudio.xstate';

// @todo: update to support overrides (for debugging) via external super admin controls
// const enableStateInspector = !isProduction;
const enableStateInspector = false;
if (enableStateInspector && typeof window !== 'undefined') {
  inspect({
    url: 'https://statecharts.io/inspect',
    iframe: false,
  });
}

const isUnitTesting = false;

/**
 * Note: the placeholder action is the minimum thing needed for an event for it to be registered.
 * This is sometimes used for stubbing out future events, or if something else is listening for those events
 */

export const placeholderAction = (): void => {};

export type { GlobalAppContext, GlobalAppEvents };

const getWindowWithPypestreamGlobals = ():
  | WindowWithPypestreamGlobals
  | (typeof globalThis & WindowWithPypestreamGlobals) => {
  const win =
    typeof window !== 'undefined'
      ? (window as WindowWithPypestreamGlobals)
      : (globalThis as typeof globalThis & WindowWithPypestreamGlobals);

  return win;
};

const win = getWindowWithPypestreamGlobals();
export const ldClient = initialize(
  import.meta.env.FE_LAUNCH_DARKLY_API_KEY,
  {
    kind: 'user',
    anonymous: true,
  },
  {
    // This is to prevent sending many events from app to launchDarkly.
    // @todo - Need to check how elegantly we can handle this as w.r.t. launch darkly docs, this might break some features on LD side. (features like, reports, ld logs and all) --> not good for AB testing.
    flushInterval: 900000,
  }
);

export const globalAppMachine = Machine<
  GlobalAppContext,
  GlobalAppStateSchema,
  GlobalAppEvents
>(
  {
    predictableActionArguments: true,
    id: 'app',
    context: {},
    strict: true,
    // switching type to parallel (in the parent machine) with no state structure,
    // seems to cause issues with child machines not being invoked? worth
    // re-checking once the state machine structure gets further built out
    type: 'parallel',
    invoke: [
      {
        id: 'initializing-feature-flags',
        src: (_ctx: GlobalAppContext, _event: GlobalAppEvents) => {
          ldClient.on('change', (updatedFlags) => {
            Object.keys(updatedFlags).forEach((updatedFlagKey) => {
              sendGlobalAppEvent({
                type: 'featureFlags.update',
                // isValidFlag check is making sure that this typecast is correct
                // @todo - Ideally we should not need this typecast, need to check for proper fix
                updatedFlag: updatedFlagKey as AvailableFeatureFlagKeys,
                updatedValue: updatedFlags[updatedFlagKey].current,
              });
            });
          });

          return (
            ldClient
              // Bounded only so a hung stream cannot wedge the app forever —
              // the catch below is what reports "LD is not coming", and it
              // never runs on a promise that never settles. Deliberately
              // generous: on expiry every flag reads as off, so a cutoff tight
              // enough to trip on a merely slow connection would silently
              // disable features for real users. Normal init is sub-second.
              .waitForInitialization(30)
              .then(() => {
                sendGlobalAppEvent({
                  type: 'featureFlags.initialized',
                  flags: Object.fromEntries(
                    Object.entries(ldClient.allFlags())
                  ) as FeatureFlagsType,
                });
              })
              .catch((err) => {
                reportError(err, { source: 'LD-initialization failed' });
                console.warn(
                  "Bypassed LD-initialization as we didn't had FFs build time."
                );
                // Report the failure as an ANSWER, not silence. Consumers read
                // `ctx.featureFlags?.[flag] ?? false`, so an empty map means
                // "everything off" — same behaviour as before. Leaving it
                // `undefined` is what is dangerous: it is indistinguishable from
                // "still loading", and any gate that waits for a real answer
                // (pype-designer-page.tsx) would spin forever (PSTUDIO-3768).
                sendGlobalAppEvent({
                  type: 'featureFlags.initialized',
                  flags: {} as FeatureFlagsType,
                });
                return Promise.resolve();
              })
          );
        },
      },
      {
        id: GLOBAL_APP_SUB_MACHINE_IDS.user,
        src: userMachine,
      },
      {
        id: GLOBAL_APP_SUB_MACHINE_IDS.proStudio,
        src: proStudioMachine,
      },
    ],
    states: {
      featureFlags: {
        initial: 'idle',
        states: {
          idle: {
            on: {
              'featureFlags.initialized': {
                target: '#app.featureFlags.loaded.withActualValue',
                actions: [
                  assign((ctx, data) => {
                    ctx.featureFlags = data.flags;
                    return ctx;
                  }),
                ],
              },
            },
          },
          loaded: {
            on: {
              'featureFlags.update': {
                actions: assign((ctx, data) => {
                  let updatedContext = ctx;
                  if (ctx.featureFlags && data.updatedFlag) {
                    updatedContext = {
                      ...ctx,
                      featureFlags: {
                        ...ctx.featureFlags,
                        [data.updatedFlag]: data.updatedValue,
                      },
                    };
                  }
                  return updatedContext;
                }),
              },
            },
            states: {
              withActualValue: {},
              withDefaultValues: {},
            },
          },
        },
      },
    },
    on: {
      // globally allow any part of the system to send a Toast message
      'app.sendUserMessage': {
        actions: (ctx, event) => sendUserMessage(event.msg),
      },
      'user.signIn': {
        actions: [
          forwardTo(userMachine.id),
          // forwardTo(managerMachine.id),
          // forwardTo(uiMachine.id),
        ],
      },
      'user.signOut': {
        actions: forwardTo(userMachine.id),
      },
      'app.log': {
        actions: [(ctx, event) => console.log(event.msg)],
      },
      noop: {
        actions: placeholderAction,
      },
    },
  },
  {
    activities: Object.fromEntries(
      Object.values(appActivities).map(({ id, exec }) => [id, exec])
    ),
    actions: Object.fromEntries(
      Object.values(globalAppActions).map(({ id, exec }) => [id, exec])
    ),
  }
);

export const globalAppService: GlobalAppServiceInterpreter = interpret<
  GlobalAppContext,
  GlobalAppStateSchema,
  GlobalAppEvents,
  GlobalAppTypestates,
  any
>(globalAppMachine, {
  devTools: enableStateInspector,
});

// boot up the global app service as early as possible
globalAppService.start();

export const { send: sendGlobalAppEvent } = globalAppService;

export type GlobalAppServiceInterpreter = Interpreter<
  GlobalAppContext,
  GlobalAppStateSchema,
  GlobalAppEvents,
  GlobalAppTypestates,
  any
>;

function log({
  state,
  name,
}: {
  name: string;
  state:
    | State<
        GlobalAppContext,
        GlobalAppEvents,
        GlobalAppStateSchema,
        GlobalAppTypestates,
        any
      >
    | State<UserContext, UserEvents, UserState, UserTypestates, any>
    | State<
        ProStudioContext,
        ProStudioEvents,
        ProStudioState,
        ProStudioTypestates,
        any
      >;
}): void {
  if (isUnitTesting) return;

  console.debug(
    `[${name}]: "${state.event.type}" Xstate Event`,
    isProduction ? state.event : state // log less in prod
  );
}

// Merge, never replace: `@pypestream/design-system`'s `smart.xstate.ts` sets
// `window.pypestream.smartService` at ITS OWN module load, which `main.tsx`'s
// very first import (`@pypestream/design-system`) triggers before this
// module is evaluated. A plain `win.pypestream = { globalAppService }` here
// silently erased that field the moment this app's module graph finished
// loading, well before `DOMContentLoaded` — the same defect Manager's own
// `app.xstate.ts` carried until PSTUDIO-3330 (review round 2, prior-art
// search across the repo found this file still doing it).
win.pypestream = {
  ...win.pypestream,
  globalAppService,
};

export const userService = globalAppService.children.get(
  GLOBAL_APP_SUB_MACHINE_IDS.user
) as UserInterpreter;

export const proStudioService = globalAppService.children.get(
  GLOBAL_APP_SUB_MACHINE_IDS.proStudio
) as ProStudioInterpreter;

interface Service {
  name: GLOBAL_APP_SUB_MACHINE_IDS;
  service: UserInterpreter | ProStudioInterpreter;
}

const services: Service[] = [
  {
    name: GLOBAL_APP_SUB_MACHINE_IDS.user,
    service: userService,
  },
  {
    name: GLOBAL_APP_SUB_MACHINE_IDS.proStudio,
    service: proStudioService,
  },
];

services.forEach(({ name, service }) => {
  if (!service) {
    throwErrorWithId(`Missing XState ${name}Service`);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  service.subscribe((state) => log({ state, name: name }));
});

export const { send: sendUserEvent } = userService;
export const { send: sendProStudioEvent } = proStudioService;

userService.subscribe((state) =>
  log({ state, name: GLOBAL_APP_SUB_MACHINE_IDS.user })
);

proStudioService.subscribe(async (state) => {
  log({ state, name: GLOBAL_APP_SUB_MACHINE_IDS.proStudio });
});

export const {
  useStateMatches: useGlobalAppStateMatches,
  useStateMatchesOneOf: useGlobalAppStateMatchesOneOf,
  useCtxSelector: useGlobalAppCtxSelector,
  useOnEvent: useGlobalAppOnEvent,
  useIsEventAllowed: useIsGlobalAppEventAllowed,
  waitForEvents: waitForGlobalAppEvents,
  useCurrentState: useGlobalAppCurrentState,
} = createAppXstateHooks(globalAppService);

export const {
  useCtxSelector: useUserCtxSelector,
  useStateMatches: useUserStateMatches,
  useStateMatchesOneOf: useUserStateMatchesOneOf,
  useOnEvent: useOnUserEvent,
  useIsEventAllowed: useIsUserEventAllowed,
  useCurrentState: useUserCurrentState,
} = createUserXstateHooks(userService);

export const {
  useCtxSelector: useProStudioCtxSelector,
  useStateMatches: useProStudioStateMatches,
  useStateMatchesOneOf: useProStudioStateMatchesOneOf,
  useStateMatchesAllOf: useProStudioStateMatchesAllOf,
  useOnEvent: useOnProStudioEvent,
  useIsEventAllowed: useIsProStudioEventAllowed,
  useCurrentState: useProStudioCurrentState,
} = createProStudioXstateHooks(proStudioService);

export const useCurrentState = () => {
  const global = useGlobalAppCurrentState();
  const user = useUserCurrentState();
  const proStudio = useProStudioCurrentState();

  return {
    global,
    user,
    proStudio,
  };
};

// export function useAppCanEdit(): boolean {
//   return useGlobalAppStateMatches('apps.selected');
// }

/**
 * This is just an example of how to create an async function that sends an event, waits for the response event and resolves promise with it.
 */
async function getAppClientData(): Promise<void | { data: unknown }> {
  const event = await waitForGlobalAppEvents({
    eventToSend: {
      type: 'app.sendUserMessage',
      msg: {
        text: 'send toast when log comes through',
      },
    },
    events: ['app.log'],
  });

  if (event.type === 'app.log') {
    const { msg } = event;
    console.log('msg', msg);
  }
}

/**
 * Analytics that require site, user, client data
 */
(() => {
  /**
   * Subscribe to xstate machines (ex. appService and appClientDataService)
   * Once app ready, can be used to fire off events, analytics, etc
   */
  globalAppService.subscribe((globalAppState) => {
    console.log('globalAppState', globalAppState);
    if (globalAppState.matches('user.loggedIn.loaded.userInfo')) {
      globalAppService.subscribe((state) => {
        if (state.matches('apps.selected')) {
          console.log(
            'example of sending analytics when a specific app was selected'
          );
          // const { site } = state.context;
          // const { siteId, appClientMeta } = site;
          // const { user } = appService.state.context;
          // analytics, etc
        }
      });
    }
  });
})();
